import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MonitorIcon, SmartphoneIcon, TabletIcon } from 'lucide-react';
export type Device = 'desktop' | 'tablet' | 'mobile' | 'iphone16';

/** Anchos reales de viewport de cada dispositivo. */
const WIDTHS: Record<Device, number | null> = {
  desktop: null,
  tablet: 768,
  mobile: 390,
  // iPhone 16 Pro Max: 440 × 956 puntos.
  iphone16: 440
};
const OPTIONS: {
  value: Device;
  label: string;
  icon: typeof MonitorIcon;
}[] = [{
  value: 'desktop',
  label: 'Escritorio',
  icon: MonitorIcon
}, {
  value: 'tablet',
  label: 'Tablet',
  icon: TabletIcon
}, {
  value: 'mobile',
  label: 'Móvil',
  icon: SmartphoneIcon
}];

/**
 * Marco de vista previa real.
 *
 * El contenido se renderiza DENTRO de un iframe cuyo ancho es el del
 * dispositivo elegido. Es la única forma de que las media queries y los
 * puntos de ruptura de Tailwind (`sm:`, `lg:`…) se resuelvan contra el ancho
 * del teléfono y no contra el de la ventana del navegador: así la vista previa
 * móvil coincide exactamente con la landing pública abierta en el móvil.
 *
 * Se usa un portal de React, de modo que el árbol sigue siendo el mismo
 * componente y cualquier cambio del borrador se refleja al instante, sin
 * guardar, recargar ni cambiar de dispositivo.
 */
function PreviewFrame({
  width,
  children,
  onBlockSelect,
  className = ''
}: {
  width: number | null;
  children: ReactNode;
  /** Selección de bloque desde dentro del iframe (ver nota más abajo). */
  onBlockSelect?: (blockId: string) => void;
  className?: string;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);
  const selectRef = useRef(onBlockSelect);
  selectRef.current = onBlockSelect;
  useEffect(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc) return;

    // Documento limpio, con el mismo meta viewport que la página pública.
    doc.open();
    doc.write('<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body></body></html>');
    doc.close();

    /** Copia dentro del iframe exactamente las mismas hojas de estilo. */
    const syncStyles = () => {
      doc.querySelectorAll('[data-ava-preview-style]').forEach(node => node.remove());
      document.querySelectorAll('style, link[rel="stylesheet"]').forEach(node => {
        const clone = node.cloneNode(true) as HTMLElement;
        clone.setAttribute('data-ava-preview-style', '');
        // Algunas herramientas insertan las reglas por CSSOM y dejan la
        // etiqueta vacía: en ese caso se copia el texto real de las reglas.
        if (clone.tagName === 'STYLE' && !clone.textContent) {
          try {
            const sheet = (node as HTMLStyleElement).sheet;
            const rules = sheet ? Array.from(sheet.cssRules).map(rule => rule.cssText) : [];
            clone.textContent = rules.join('\n');
          } catch {
            // Hoja de otro origen: no se puede leer, se ignora.
          }
        }
        doc.head.appendChild(clone);
      });
    };
    syncStyles();

    // Estilos mínimos del documento del iframe.
    const base = doc.createElement('style');
    base.textContent = 'html,body{margin:0;padding:0;background:#fff;overflow-x:hidden}';
    doc.head.appendChild(base);
    const root = doc.createElement('div');
    doc.body.appendChild(root);
    setMountNode(root);

    /*
      Los eventos de un portal alojado en otro documento no llegan a la raíz de
      React, así que la selección de bloque se traduce a mano: se escucha el
      clic en el documento del iframe y se busca el bloque más cercano.
    */
    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const node = target?.closest?.('[data-block-id]');
      const id = node?.getAttribute('data-block-id');
      if (id) selectRef.current?.(id);
    };
    doc.addEventListener('click', onClick);

    // Si la aplicación añade estilos después (fuentes, utilidades nuevas), se
    // vuelven a copiar. Se agrupa con un microtask para no repetirlo N veces.
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        syncStyles();
      });
    });
    observer.observe(document.head, {
      childList: true,
      subtree: true
    });
    return () => {
      observer.disconnect();
      doc.removeEventListener('click', onClick);
      setMountNode(null);
    };
  }, []);
  return <>
      <iframe ref={frameRef} title="Vista previa de la landing" className={`block h-full border-0 bg-background ${className}`} style={{
      width: width ? `${width}px` : '100%',
      maxWidth: '100%'
    }} data-id="element-1025" data-mp="28synka2lkyit" />
      {mountNode && createPortal(children, mountNode)}
    </>;
}
interface DevicePreviewProps {
  device: Device;
  onDeviceChange: (device: Device) => void;
  children: ReactNode;
  toolbar?: ReactNode;
  /** Se llama al pulsar un bloque dentro de la vista previa. */
  onBlockSelect?: (blockId: string) => void;
  /**
   * Renderiza la vista previa dentro de un iframe del ancho del dispositivo.
   * Es lo que hace que la previsualización móvil coincida exactamente con el
   * teléfono real. Se usa en el editor de landings, donde lo importante es la
   * fidelidad visual. En el editor de formularios se deja desactivado para
   * poder escribir en los campos de la vista previa.
   */
  isolate?: boolean;
}

/**
 * Vista previa por dispositivo. Además de los tres anchos habituales incluye
 * una simulación de iPhone 16 Pro Max (440 × 956) con marco, isla dinámica e
 * indicador inferior, para revisar la landing tal y como la verá un visitante
 * desde el móvil.
 */
export function DevicePreview({
  device,
  onDeviceChange,
  children,
  toolbar,
  onBlockSelect,
  isolate = false
}: DevicePreviewProps) {
  const isPhoneFrame = device === 'iphone16';
  /** Contenido de la vista previa, con o sin aislamiento en iframe. */
  const surface = (width: number | null) => isolate ? <PreviewFrame width={width} onBlockSelect={onBlockSelect} data-id="element-1026" data-mp="1g463pdkhltk">
        {children}
      </PreviewFrame> : <div className="h-full overflow-y-auto" style={{
    width: width ? `${width}px` : '100%',
    maxWidth: '100%'
  }} data-id="element-1027" data-mp="3smx4ymkw18ux">
        {children}
      </div>;
  return <div className="flex h-full min-w-0 flex-col rounded-lg border border-border bg-surface" data-id="element-1028" data-mp="p56ncw4ahcnj">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2" data-id="element-1029" data-mp="k5k8ki8tbocm">
        <div className="flex flex-wrap gap-1 rounded-md bg-background p-1" role="group" aria-label="Tamaño de la vista previa" data-id="element-1030" data-mp="f6zg54zsy8yq">
          {OPTIONS.map(option => <button key={option.value} type="button" onClick={() => onDeviceChange(option.value)} aria-pressed={device === option.value} aria-label={option.label} title={option.label} className={`flex h-8 w-9 items-center justify-center rounded-sm transition-colors ${device === option.value ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-surface'}`} data-id="element-1031" data-mp="34xclm2kto9l4">
              <option.icon className="h-4 w-4" data-id="element-1032" data-mp="2zq645ecpmeha" />
            </button>)}
          <button type="button" onClick={() => onDeviceChange('iphone16')} aria-pressed={isPhoneFrame} title="iPhone 16 Pro Max" className={`flex h-8 items-center justify-center gap-1.5 rounded-sm px-2.5 text-[11px] font-medium transition-colors ${isPhoneFrame ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-surface'}`} data-id="element-1033" data-mp="34hnw5cwr94q4">
            <SmartphoneIcon className="h-4 w-4" data-id="element-1034" data-mp="3ij08zfgmcq7s" />
            iPhone 16 Pro Max
          </button>
        </div>
        {toolbar}
      </div>
      <div className="flex-1 overflow-auto p-4" data-id="element-1035" data-mp="3neflo42w4g2m">
        {isPhoneFrame ?
      // Marco del teléfono: no recorta el contenido, solo lo enmarca.
      <div className="mx-auto w-full max-w-[472px]" data-id="element-1036" data-mp="1t3sfu0u46pxb">
            <div className="relative mx-auto rounded-[3rem] border border-border bg-navy p-3 shadow-xl" data-id="element-1037" data-mp="1t4lnr54t7lh1">
              <div className="relative overflow-hidden rounded-[2.4rem] bg-background" data-id="element-1038" data-mp="3q17vm6yqydza">
                <div className="pointer-events-none absolute left-1/2 top-2 z-20 h-6 w-24 -translate-x-1/2 rounded-full bg-navy" aria-hidden="true" data-id="element-1039" data-mp="3pzdk1j9d7y7v" />
                <div className="mx-auto h-[956px] max-h-[70vh] w-full max-w-[440px]" data-id="element-1040" data-mp="3mojkgx8vavz">
                  {/* La clave de la fidelidad: el iframe mide 440 px de ancho,
                      así que dentro se aplican las clases de móvil reales. */}
                  {surface(WIDTHS.iphone16)}
                </div>
                <div className="pointer-events-none absolute bottom-2 left-1/2 z-20 h-1 w-28 -translate-x-1/2 rounded-full bg-navy/40" aria-hidden="true" data-id="element-1041" data-mp="3l3oixbhp699g" />
              </div>
            </div>
            <p className="mt-3 text-center text-xs text-muted-foreground" data-id="element-1042" data-mp="10al0jmwlg87q">
              iPhone 16 Pro Max · 440 × 956 px
            </p>
          </div> : <div className="mx-auto h-full overflow-hidden rounded-lg border border-border bg-background shadow-sm transition-all" style={{
        width: WIDTHS[device] ? `${WIDTHS[device]}px` : '100%',
        maxWidth: '100%'
      }} data-id="element-1043" data-mp="13ttm54w42jd2">
            {surface(WIDTHS[device])}
          </div>}
      </div>
    </div>;
}